import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Activity, CheckCircle2, Info, LayoutGrid, Power, Rocket, Wifi, WifiOff, XCircle } from "lucide-react";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api, errMsg, fmtTs, inr, num, tone } from "@/lib/api";
import { useAuth } from "@/lib/auth";

export const Card = ({ label, value, sub, cls = "text-white", tid }) => (
  <div className="panel p-4" data-testid={tid}>
    <div className="widget-title">{label}</div>
    <div className={`font-mono text-2xl mt-1 ${cls}`}>{value}</div>
    {sub && <div className="text-[11px] text-slate-500 mt-1">{sub}</div>}
  </div>
);

export const Dot = ({ ok, warn }) => <span className={`inline-block w-2 h-2 rounded-full ${ok ? "bg-[#22A34A] pulse-dot" : warn ? "bg-amber-400" : "bg-[#FF3B30]"}`} />;

export default function Dashboard({ go }) {
  const { isAdmin } = useAuth();
  const [d, setD] = useState(null);
  const [pf, setPf] = useState(null);
  const [lr, setLr] = useState(null);
  const load = () => {
    api.get("/nexfin/dashboard").then((r) => setD(r.data)).catch(() => {});
    api.get("/nexfin/preflight").then((r) => setPf(r.data)).catch(() => {});
    api.get("/nexfin/live-runs").then((r) => setLr(r.data)).catch(() => {});
  };
  useEffect(() => { load(); const t = setInterval(load, 3000); return () => clearInterval(t); }, []);
  const kill = async () => {
    if (!window.confirm("KILL SWITCH: exit all positions and stop all strategies?")) return;
    try { const r = await api.post("/nexfin/kill"); toast.success(`Kill switch: ${r.data.stopped} run(s) stopped`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const emailMe = async () => {
    try { const r = await api.post("/nexfin/email-test"); toast.success(`Daily P&L emailed to ${r.data.to}`); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!d) return <div className="text-slate-500 text-sm p-4">Loading…</div>;
  const b = d.broker, running = d.running[0];
  const isDemo = String(b.feed_mode || "").toUpperCase() !== "LIVE";
  const funds = d.funds || {};
  const avail = funds.paper ? funds.available : Number((Array.isArray(funds) ? funds[0] : funds)?.net ?? (Array.isArray(funds) ? funds[0] : funds)?.cash ?? NaN);
  return (
    <div className="space-y-3 rise" data-testid="nexfin-dashboard">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="font-display text-xl font-bold text-white tracking-tight">Dashboard</h1>
        <span className="flex items-center gap-1.5 text-xs font-mono text-slate-300" data-testid="dash-broker-status">
          {b.connected ? <Wifi className="w-3.5 h-3.5 text-[#22A34A]" /> : <WifiOff className="w-3.5 h-3.5 text-[#FF3B30]" />} Broker {b.connected ? `· ${b.client_id}` : "· not logged in"}
        </span>
        <span className="flex items-center gap-1.5 text-xs font-mono text-slate-300" data-testid="dash-market-status"><Dot ok={d.market.open} warn={d.market.replay.running} /> Market {d.market.replay.running ? `REPLAY ${d.market.replay.index}/${d.market.replay.total}` : d.market.open ? "OPEN" : "CLOSED"} · feed {b.feed_mode}</span>
        {!d.live_enabled && <span className="text-[10px] font-mono px-2 py-0.5 border border-amber-500/40 text-amber-400 rounded-sm">LIVE TRADING DISABLED</span>}
        <button data-testid="dash-email-pnl" onClick={emailMe} className="btn-ghost h-8 ml-auto">Email me today's P&L</button>
        <button data-testid="dash-kill-switch" onClick={kill} className="btn-bear h-8"><Power className="w-3.5 h-3.5" /> Kill Switch</button>
      </div>
      {isDemo && (
        <div className="flex items-start gap-3 p-3 rounded-sm border border-amber-500/40 bg-amber-500/10" data-testid="demo-data-banner">
          <Info className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
          <div className="text-xs text-amber-200/90 leading-relaxed">
            <span className="font-semibold text-amber-300">Demo data — simulated prices.</span> You're {d.market.replay.running ? "on the replay feed" : "not connected to a live market feed"}, so every price, chart and paper fill below is <span className="font-semibold">generated for practice</span> and does not reflect the real market. {isAdmin
              ? <>Open <button className="underline hover:text-amber-100" data-testid="demo-connect-broker" onClick={() => go("broker")}>Broker &amp; Data</button> and log in to Alice Blue to switch to real-time quotes and charts.</>
              : <>Your administrator must connect the Alice Blue broker feed to enable real-time market data.</>}
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card label="Today's P&L" value={inr(d.today_pnl)} sub={`${d.today_trades} closed trade(s)`} cls={tone(d.today_pnl)} tid="dash-pnl" />
        <Card label="Running strategy" value={running ? running.label : "—"} sub={running ? `${running.instrument.underlying} · ${running.params.candle_timeframe}m · ${running.mode}` : "Start one from Strategies"} cls="text-white text-lg" tid="dash-running" />
        <Card label="Open positions" value={d.open_trades.length} sub={d.open_trades[0] ? `${d.open_trades[0].side} ${d.open_trades[0].symbol} @ ${num(d.open_trades[0].entry_price)}` : "flat"} tid="dash-open" />
        <Card label="Funds" value={Number.isFinite(avail) ? inr(avail, 0) : funds.error ? "—" : "—"} sub={funds.paper ? "Paper account" : funds.error || "AliceBlue limits"} tid="dash-funds" />
      </div>
      {lr?.runs?.length > 0 && (
        <div className="panel p-3" data-testid="dash-heatmap">
          <div className="flex items-center gap-2 mb-2">
            <LayoutGrid className="w-3.5 h-3.5 text-[#22A34A]" />
            <span className="widget-title">Live bots</span>
            <span className="text-[10px] font-mono text-slate-400">{lr.agg.total} running · {lr.agg.in_position} in position · realized today {inr(lr.agg.realized_today)}{lr.agg.win_rate != null && ` · ${lr.agg.win_rate}% win`}</span>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-1.5" data-testid="heatmap-grid">
            {lr.runs.map((r) => {
              const pos = !["FLAT", "PENDING LONG", "PENDING SHORT"].includes(r.side);
              const up = (r.open_pts || 0) >= 0;
              const bg = !pos ? "bg-[#0E2440] border-[#163056]" : up ? "bg-[#22A34A]/15 border-[#22A34A]/50" : "bg-[#FF3B30]/15 border-[#FF3B30]/50";
              return (
                <button key={r.run_id} data-testid={`heatmap-${r.symbol}`} onClick={() => go("live")} className={`text-left px-2 py-1.5 rounded-sm border ${bg} transition-colors hover:brightness-125`}>
                  <div className="font-mono text-[11px] font-bold text-slate-100 truncate">{r.symbol}</div>
                  <div className={`font-mono text-[10px] ${!pos ? "text-slate-500" : tone(r.open_pts)}`}>{pos ? `${r.side} ${r.open_pts >= 0 ? "+" : ""}${num(r.open_pts)}` : r.side}</div>
                </button>
              );
            })}
          </div>
        </div>
      )}
      {pf && (
        <div className="panel p-3" data-testid="dash-preflight">
          <div className="flex items-center gap-2 mb-2">
            <Rocket className="w-3.5 h-3.5 text-[#22A34A]" />
            <span className="widget-title">Go-live readiness</span>
            <span className={`text-[10px] font-mono px-2 py-0.5 rounded-sm ${pf.ready ? "bg-[#22A34A]/15 text-[#22A34A]" : "bg-amber-500/15 text-amber-400"}`} data-testid="preflight-status">{pf.ready ? "READY" : "NOT READY"}</span>
            <span className="text-[10px] font-mono text-slate-500 ml-auto">Live order placement: {pf.live_enabled ? "ENABLED" : "DISABLED"}</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1.5">
            {pf.checks.map((c) => (
              <div key={c.key} className="flex items-start gap-2 text-xs px-2 py-1.5 bg-[#071525] border border-[#163056] rounded-sm" data-testid={`preflight-${c.key}`}>
                {c.ok ? <CheckCircle2 className="w-4 h-4 text-[#22A34A] shrink-0 mt-0.5" /> : <XCircle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />}
                <div className="min-w-0">
                  <div className="text-slate-200 font-medium">{c.label}</div>
                  <div className="text-[10px] font-mono text-slate-500 truncate">{c.detail}{!c.ok && ` · ${c.hint}`}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 xl:grid-cols-[1.4fr_1fr] gap-3">
        <div className="panel p-3" data-testid="dash-pnl-curve">
          <div className="widget-title mb-2">Day P&L curve</div>
          <div className="h-48">
            {d.pnl_curve.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={d.pnl_curve}>
                  <defs><linearGradient id="pg" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#22A34A" stopOpacity={0.5} /><stop offset="100%" stopColor="#22A34A" stopOpacity={0} /></linearGradient></defs>
                  <CartesianGrid stroke="#163056" strokeDasharray="3 3" />
                  <XAxis dataKey="time" tickFormatter={(t) => fmtTs(t).slice(-5)} tick={{ fill: "#64748B", fontSize: 10 }} />
                  <YAxis tick={{ fill: "#64748B", fontSize: 10 }} width={60} />
                  <Tooltip contentStyle={{ background: "#0B1F3A", border: "1px solid #163056", fontSize: 11 }} formatter={(v) => inr(v)} labelFormatter={fmtTs} />
                  <Area type="stepAfter" dataKey="pnl" stroke="#22A34A" fill="url(#pg)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            ) : <div className="h-full grid place-items-center text-xs text-slate-500">No closed trades yet today</div>}
          </div>
        </div>
        <div className="panel" data-testid="dash-alerts">
          <div className="px-3 py-2 border-b border-[#163056] widget-title flex items-center gap-2"><Activity className="w-3 h-3" /> Alerts</div>
          <div className="max-h-48 overflow-y-auto">
            {d.alerts.map((a) => (
              <div key={a.id} className="px-3 py-1.5 border-b border-[#102A4A] text-[11px] font-mono flex gap-2">
                <span className="text-slate-500 shrink-0">{fmtTs(a.time)}</span>
                <span className={a.level === "ERROR" ? "text-[#FF3B30]" : a.level === "WARN" ? "text-amber-400" : a.level === "BUY" ? "text-[#22A34A]" : "text-slate-300"}>{a.message}</span>
              </div>
            ))}
            {!d.alerts.length && <div className="p-4 text-xs text-slate-500">No alerts yet</div>}
          </div>
        </div>
      </div>
      <div className="panel" data-testid="dash-orders">
        <div className="px-3 py-2 border-b border-[#163056] widget-title">Recent orders</div>
        <table className="tbl w-full"><thead><tr><th>Time</th><th>Symbol</th><th>Side</th><th>Qty</th><th>Price</th><th>Mode</th><th>Status</th><th>Broker id</th></tr></thead>
          <tbody>{d.recent_orders.map((o) => (
            <tr key={o.id}><td className="text-slate-500">{fmtTs(o.time)}</td><td className="text-slate-100">{o.symbol}</td><td className={o.side === "BUY" ? "text-bull" : "text-bear"}>{o.side}</td><td>{o.qty}</td><td>{num(o.price)}</td>
              <td className={o.mode === "LIVE" ? "text-[#FF3B30]" : "text-amber-400"}>{o.mode}</td><td>{o.status}</td><td className="text-slate-500">{(o.broker_order_ids || []).join(", ")}</td></tr>
          ))}{!d.recent_orders.length && <tr><td colSpan={8} className="text-center text-slate-500 py-4">No orders yet</td></tr>}</tbody></table>
      </div>
      {isAdmin && <p className="text-[11px] text-slate-500">Admin: use <button className="text-[#22A34A] hover:underline" onClick={() => go("broker")}>Broker & Data</button> to login for today or run the replay feed.</p>}
    </div>
  );
}
